/**
 * Image lightbox modal with keyboard navigation (arrows, Escape).
 * On desktop: expand-from-thumbnail animation. On mobile: direct open.
 */

import { portfolioItems } from './data/portfolioFeed.js';
import { MODAL_FADE_DURATION_MS } from './constants.js';

let currentModalIndex = -1;

function findImageIndex(imageSrc) {
  return portfolioItems.findIndex((item) => item.image === imageSrc);
}

/**
 * Open the image modal.
 * @param {string} imageSrc - Image URL
 * @param {number} [index] - Item index for keyboard nav
 * @param {HTMLElement} [sourceElement] - Thumbnail element for expand animation
 */
export function openModal(imageSrc, index, sourceElement) {
  const imageModal = document.getElementById('imageModal');
  const modalImage = document.getElementById('modalImage');
  if (!imageModal || !modalImage) return;

  currentModalIndex = index !== undefined ? index : findImageIndex(imageSrc);
  const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || window.innerWidth <= 768;

  if (isMobile) {
    modalImage.src = imageSrc;
    imageModal.classList.add('active');
    document.body.style.overflow = 'hidden';
    return;
  }

  if (sourceElement) {
    const rect = sourceElement.getBoundingClientRect();
    const img = sourceElement.querySelector('img');

    modalImage.src = imageSrc;

    const setupAnimation = () => {
      modalImage.style.position = 'fixed';
      modalImage.style.left = `${rect.left}px`;
      modalImage.style.top = `${rect.top}px`;
      modalImage.style.width = `${rect.width}px`;
      modalImage.style.height = `${rect.height}px`;
      modalImage.style.objectFit = 'cover';
      modalImage.style.borderRadius = '0.75rem';
      modalImage.style.opacity = '1';
      modalImage.style.transition = 'none';

      imageModal.classList.add('active');
      document.body.style.overflow = 'hidden';
      void modalImage.offsetWidth;

      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      const targetWidth = Math.min(window.innerWidth - 4 * 16, 1200);
      const targetHeight = (targetWidth * 3) / 4;

      requestAnimationFrame(() => {
        modalImage.style.transition = 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
        modalImage.style.left = `${centerX - targetWidth / 2}px`;
        modalImage.style.top = `${centerY - targetHeight / 2}px`;
        modalImage.style.width = `${targetWidth}px`;
        modalImage.style.height = `${targetHeight}px`;
        modalImage.style.objectFit = 'contain';
        modalImage.style.borderRadius = '0';
      });
    };

    if (img?.complete && img.naturalWidth > 0) {
      setupAnimation();
    } else {
      const tempImg = new Image();
      tempImg.onload = setupAnimation;
      tempImg.src = imageSrc;
    }
  } else {
    modalImage.src = imageSrc;
    imageModal.classList.add('active');
    document.body.style.overflow = 'hidden';
  }
}

/**
 * Close the modal and reset state.
 */
export function closeModal() {
  const imageModal = document.getElementById('imageModal');
  const modalImage = document.getElementById('modalImage');
  if (!imageModal) return;

  if (modalImage) {
    modalImage.style.position = '';
    modalImage.style.left = '';
    modalImage.style.top = '';
    modalImage.style.width = '';
    modalImage.style.height = '';
    modalImage.style.objectFit = '';
    modalImage.style.borderRadius = '';
    modalImage.style.transition = '';
    modalImage.style.opacity = '';
    modalImage.style.transform = '';
  }

  imageModal.classList.remove('active');
  document.body.style.overflow = '';
  currentModalIndex = -1;
}

/**
 * Navigate modal to previous/next image.
 * @param {number} direction - -1 for previous, 1 for next
 */
export function navigateModal(direction) {
  if (currentModalIndex === -1) return;

  let targetIndex = currentModalIndex + direction;
  if (targetIndex < 0) targetIndex = portfolioItems.length - 1;
  else if (targetIndex >= portfolioItems.length) targetIndex = 0;

  const targetItem = portfolioItems[targetIndex];
  if (!targetItem) return;

  const imageModal = document.getElementById('imageModal');
  const modalImage = document.getElementById('modalImage');
  if (!imageModal || !modalImage) return;

  modalImage.style.opacity = '0';
  modalImage.style.transition = 'opacity 0.2s ease';

  setTimeout(() => {
    modalImage.src = targetItem.image;
    currentModalIndex = targetIndex;
    modalImage.style.position = '';
    modalImage.style.left = '';
    modalImage.style.top = '';
    modalImage.style.width = '';
    modalImage.style.height = '';
    modalImage.style.objectFit = '';
    modalImage.style.borderRadius = '';
    requestAnimationFrame(() => {
      modalImage.style.opacity = '1';
    });
  }, MODAL_FADE_DURATION_MS);
}

/**
 * Set up modal event listeners: close button, backdrop click, Escape/Arrow keys.
 * Call once on DOMContentLoaded.
 */
export function setupModalListeners() {
  const imageModal = document.getElementById('imageModal');
  const modalClose = document.getElementById('modalClose');

  if (modalClose) modalClose.addEventListener('click', closeModal);

  if (imageModal) {
    imageModal.addEventListener('click', (e) => {
      if (e.target === imageModal) closeModal();
    });
  }

  document.addEventListener('keydown', (e) => {
    if (!imageModal?.classList.contains('active')) return;
    if (e.key === 'Escape') closeModal();
    else if (e.key === 'ArrowLeft') navigateModal(-1);
    else if (e.key === 'ArrowRight') navigateModal(1);
  });
}
